/**
 * Persist an accepted ``suggest_outline_changes`` proposal: the same backend
 * endpoint the web app's Accept card uses, called from the Word pane. The
 * types mirror the web app's `agents-v2/api/outlineProposal.ts`.
 */
export type OutlineProposalChange = {
  op: "add" | "delete" | "reorder" | "renumber" | "update";
  section_id?: string | null;
  insert_after_section_id?: string | null;
  insert_index?: number | null;
  section_ids?: string[] | null;
  section_number?: string | null;
  section?: Record<string, unknown> | null;
};

export type ApplyOutlineProposalResponse = {
  status: string;
  persisted?: boolean;
  outline?: unknown[];
  message?: string;
};

const API = process.env.REACT_APP_API_URL ?? "";

/**
 * Apply proposal ops to the bid's outline.
 * @param bidId - Bid id
 * @param changes - Proposal ops from the tool result
 * @param token - Auth JWT
 * @param summary - Optional proposal summary
 * @returns Apply response with the updated outline when successful
 */
export async function applyOutlineProposal(
  bidId: string,
  changes: OutlineProposalChange[],
  token: string,
  summary?: string
): Promise<ApplyOutlineProposalResponse> {
  const response = await fetch(`${API}/apply_outline_proposal`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ bid_id: bidId, changes, ...(summary ? { summary } : {}) }),
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(detail || `Failed to apply outline proposal (${response.status})`);
  }
  return (await response.json()) as ApplyOutlineProposalResponse;
}
